import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Loader2 } from "lucide-react";

import { useTran } from "@/hooks";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCompanyStore } from "../hooks";
import CompanySetupPage from "../pages/CompanySetupPage";

const CompanySetupDialog = observer(function CompanySetupDialog({
  open,
  user,
  onClose,
  onCompleted,
  showTargetFrequency = false,
}) {
  const { t } = useTran();
  const { fetchCompany, company, reset, setCompany, companyLoading } =
    useCompanyStore();

  const companyId = useMemo(
    () =>
      user?.companyId ??
      user?.company?.id ??
      user?.company?._id ??
      user?.company?.companyId ??
      user?.companyDetails?.id ??
      user?.companyDetails?._id ??
      user?.companyDetails?.companyId ??
      null,
    [user],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!companyId) {
      reset({ persist: false });
      setCompany(null);
      return;
    }

    fetchCompany({ force: true, companyId }).catch(() => {});
  }, [open, companyId, fetchCompany, reset, setCompany]);

  const handleClose = () => {
    onClose?.();
  };

  const handleCompleted = (savedCompany) => {
    onCompleted?.(savedCompany);
    handleClose();
  };

  const isLoading = open && Boolean(companyId) && companyLoading;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="h-[95vh] w-[calc(100vw-1rem)] max-w-[calc(100vw-2rem)] overflow-hidden p-0 sm:w-full sm:max-w-4xl">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-2 p-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t("Loading company details...")}
            </span>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-6">
            <CompanySetupPage
              isDialog
              companyId={companyId}
              companyData={company}
              showTargetFrequency={showTargetFrequency}
              compact={false}
              onCompleted={handleCompleted}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default CompanySetupDialog;
